import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router';

import { MainNavigation } from '@/app/shell/MainNavigation';
import { RealtimeConnectionStatusBar } from '@/app/shell/RealtimeConnectionStatusBar';

const MobileFooterContainerContext = createContext<HTMLDivElement | null>(null);
const ContentOverlayContainerContext = createContext<HTMLDivElement | null>(null);

export function useMobileFooterContainer(): HTMLDivElement | null {
  return useContext(MobileFooterContainerContext);
}

export function NavContentOverlayPortal({ children }: { children: ReactNode }) {
  const container = useContext(ContentOverlayContainerContext);
  return container === null ? null : createPortal(children, container);
}

export function NavContentViewport({ children }: { children: ReactNode }) {
  const [contentOverlayContainer, setContentOverlayContainer] = useState<HTMLDivElement | null>(null);

  return (
    <ContentOverlayContainerContext.Provider value={contentOverlayContainer}>
      <div className="nav-layout__content">
        <div className="nav-layout__page">{children}</div>
        <div className="nav-layout__content-overlay" ref={setContentOverlayContainer} />
      </div>
    </ContentOverlayContainerContext.Provider>
  );
}

function useFocusH1OnNavigate() {
  const location = useLocation();

  useEffect(() => {
    document.querySelector<HTMLElement>('h1')?.focus();
  }, [location.pathname]);
}

export function NavLayout({ children }: { children: ReactNode }) {
  useFocusH1OnNavigate();
  const [mobileFooterContainer, setMobileFooterContainer] = useState<HTMLDivElement | null>(null);

  return (
    <MobileFooterContainerContext.Provider value={mobileFooterContainer}>
      <div className="nav-layout">
        <div className="nav-layout__nav-menu">
          <MainNavigation />
        </div>
        <RealtimeConnectionStatusBar />
        <NavContentViewport>{children}</NavContentViewport>
        <div className="nav-layout__mobile-footer" ref={setMobileFooterContainer} />
      </div>
    </MobileFooterContainerContext.Provider>
  );
}
